// INC2-CONTACT: Connections as 36 px rows (integrations design §2.10, F4), and the one row shape
// of Team › Connections (CONNECTIONS-AREA, connections design §C): provider and label, health,
// what it may do (its tiers, or a key's host), who connected it and when it was last checked,
// its grants as chips (hover names each with its level), and hover icons Check · Refresh · Grant
// · Grants · Reconnect · Disconnect. A row opens the drawer (connection-drawer.tsx). Used by Team
// › Connections (company and Mine) and My subscriptions › Connections (a person's own: grants
// only to named DEs).
import {
  Badge,
  ListRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  WorkerBadge,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import {
  KeyRoundIcon,
  PlugIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  ShieldCheckIcon,
  UnplugIcon,
  UserPlusIcon,
} from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { ConfirmRowAction } from "../confirm-row-action";
import { formatDateTime } from "../format";
import { RowAction, RowActions } from "../settings/row-actions";
import { HEALTH, healthKey, NEEDS_RECONNECT } from "../subscriptions/copy";
import type { Connection } from "../subscriptions/queries";
import { useConnectionActions } from "./actions";
import { ConnectionDrawer, type DrawerTab } from "./connection-drawer";
import { GrantDialog } from "./grant-dialog";
import { type Grant, grantsQuery, LEVEL_LABEL, PROVIDER_NAME, useGrantees } from "./grants";

export function ConnectionRows({
  api,
  co,
  slug,
  rows,
  empty,
  label,
}: {
  api: Api;
  co: string;
  slug: string;
  rows: Connection[];
  /** The honest empty line ("No company connections"). */
  empty: string;
  /** The list's accessible name. */
  label: string;
}) {
  const act = useConnectionActions(api, co, slug);
  const grants = useQuery(grantsQuery(api, co, {}));
  const names = useGrantees(api, co, rows.length > 0);
  const [open, setOpen] = useState<{ id: string; tab: DrawerTab } | null>(null);
  const [granting, setGranting] = useState<Connection | null>(null);
  // Who connected it: the person, by their user id (the vendor identity is shown beside it)
  const byUser = new Map(names.people.map((p) => [p.user_id, p.display_name] as const));
  const byConn = new Map<string, Grant[]>();
  for (const g of grants.data?.data ?? [])
    byConn.set(g.connection_id, [...(byConn.get(g.connection_id) ?? []), g]);
  // Disconnected rows stay listed (a record, as before), after the live ones, without controls.
  const ordered = [
    ...rows.filter((r) => r.state !== "disconnected"),
    ...rows.filter((r) => r.state === "disconnected"),
  ];
  const selected = rows.find((r) => r.id === open?.id && r.state !== "disconnected") ?? null;
  if (ordered.length === 0)
    return (
      <p className="px-1 py-3 text-[13px] text-muted-foreground" data-slot="connections-empty">
        {empty}
      </p>
    );
  return (
    <>
      <ul
        aria-label={label}
        className="rounded-lg border border-border"
        data-slot="connection-rows"
      >
        {ordered.map((c) => {
          const key = healthKey(c);
          const h = HEALTH[key] ?? { label: c.state, tone: "neutral" as const, sentence: "" };
          const oauth = c.provider !== "api_key";
          const signIn = oauth && NEEDS_RECONNECT.has(c.state);
          const held = byConn.get(c.id) ?? [];
          if (c.state === "disconnected")
            return (
              <li key={c.id}>
                <ListRow
                  data-connection={c.id}
                  data-health={key}
                  done
                  lead={<PlugIcon aria-hidden="true" className="size-4 text-muted-foreground" />}
                  title={c.label}
                  chips={<Badge variant={h.tone}>{h.label}</Badge>}
                />
              </li>
            );
          return (
            <li key={c.id}>
              <ListRow
                data-connection={c.id}
                data-health={key}
                selected={open?.id === c.id}
                lead={<PlugIcon aria-hidden="true" className="size-4 text-muted-foreground" />}
                title={c.label}
                onSelect={() => setOpen({ id: c.id, tab: "health" })}
                chips={
                  <>
                    <Badge variant="outline">{PROVIDER_NAME[c.provider] ?? c.provider}</Badge>
                    <Badge variant={h.tone} data-slot="health">
                      {h.label}
                    </Badge>
                    {c.tiers.slice(0, 2).map((t) => (
                      <Badge key={t} variant="neutral" className="max-md:hidden" data-slot="tier">
                        {t}
                      </Badge>
                    ))}
                  </>
                }
                meta={
                  <span className="flex min-w-0 items-center gap-3 max-md:hidden">
                    <span className="truncate font-mono">
                      {c.config?.host ??
                        c.identity_display ??
                        (c.key_last4 ? `···${c.key_last4}` : "")}
                    </span>
                    {byUser.get(c.created_by_user_id) ? (
                      <span className="truncate" data-slot="connected-by">
                        by {byUser.get(c.created_by_user_id)}
                      </span>
                    ) : null}
                    <span className="shrink-0" data-slot="last-checked">
                      {c.last_checked_at
                        ? `checked ${formatDateTime(c.last_checked_at)}`
                        : "never checked"}
                    </span>
                  </span>
                }
                trailing={
                  <span className="flex items-center gap-3 text-xs text-muted-foreground">
                    {grants.isPending ? (
                      <span data-slot="grant-count">…</span>
                    ) : grants.isError ? (
                      <span data-slot="grant-count">Grants unavailable</span>
                    ) : (
                      <GrantChips grants={held} names={names} />
                    )}
                  </span>
                }
                actions={
                  <RowActions>
                    <RowAction
                      icon={ShieldCheckIcon}
                      label={`Check ${c.label}`}
                      tip="Check"
                      disabled={act.check.isPending}
                      onClick={() => act.check.mutate(c)}
                    />
                    {oauth && !signIn ? (
                      <RowAction
                        icon={RefreshCwIcon}
                        label={`Refresh ${c.label}`}
                        tip="Refresh"
                        disabled={act.renew.isPending}
                        onClick={() => act.renew.mutate(c)}
                      />
                    ) : null}
                    <RowAction
                      icon={UserPlusIcon}
                      label={`Grant ${c.label}`}
                      tip="Grant"
                      onClick={() => setGranting(c)}
                    />
                    <RowAction
                      icon={KeyRoundIcon}
                      label={`Grants of ${c.label}`}
                      tip="Grants"
                      onClick={() => setOpen({ id: c.id, tab: "grants" })}
                    />
                    {signIn ? (
                      <RowAction
                        icon={RotateCcwIcon}
                        label={`Reconnect ${c.label}`}
                        tip="Reconnect"
                        disabled={act.reconnect.isPending}
                        onClick={() => act.reconnect.mutate(c)}
                      />
                    ) : null}
                    <ConfirmRowAction
                      icon={UnplugIcon}
                      label={`Disconnect ${c.label}`}
                      tip="Disconnect"
                      title={`Disconnect ${c.label}?`}
                      description={
                        oauth
                          ? "Its tokens are given back to the service and deleted now; every grant on it ends."
                          : "Its sealed key is destroyed now; every grant on it ends."
                      }
                      confirmLabel="Disconnect"
                      disabled={act.disconnect.isPending}
                      onConfirm={() => act.disconnect.mutate(c)}
                    />
                  </RowActions>
                }
              />
            </li>
          );
        })}
      </ul>
      {selected ? (
        <ConnectionDrawer
          api={api}
          co={co}
          slug={slug}
          connection={selected}
          grants={byConn.get(selected.id) ?? []}
          grantsState={grants}
          names={names}
          tab={open?.tab ?? "health"}
          onTab={(tab) => setOpen((o) => (o ? { ...o, tab } : o))}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {granting ? (
        <GrantDialog
          api={api}
          co={co}
          connection={granting}
          names={names}
          open
          onOpenChange={(o) => (o ? null : setGranting(null))}
        />
      ) : null}
    </>
  );
}

/** Grants as chips: up to two grantees by name, then "+n"; hover names each with its level. */
function GrantChips({ grants, names }: { grants: Grant[]; names: ReturnType<typeof useGrantees> }) {
  if (grants.length === 0)
    return (
      <span data-slot="grant-count" data-count={0}>
        No grants
      </span>
    );
  const shown = grants.slice(0, 2);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          // A button, so keyboard users reach the tooltip (Codex r1 #13)
          <button
            type="button"
            data-slot="grant-count"
            data-count={grants.length}
            aria-label={grants.length === 1 ? "1 grant" : `${grants.length} grants`}
            className="flex cursor-default items-center gap-1 rounded-sm focus-visible:outline-2 focus-visible:outline-ring"
          />
        }
      >
        {shown.map((g) => {
          const who = names.of(g);
          return (
            <Badge key={g.id} variant="neutral" data-slot="grant-chip" className="max-w-32">
              {who.worker ? <WorkerBadge kind={who.worker} size="sm" /> : null}
              <span className="truncate">{who.name}</span>
            </Badge>
          );
        })}
        {grants.length > shown.length ? (
          <span className="tabular-nums">+{grants.length - shown.length}</span>
        ) : null}
      </TooltipTrigger>
      <TooltipContent>
        <ul className="flex flex-col gap-0.5">
          {grants.map((g) => {
            const who = names.of(g);
            return (
              <li key={g.id} className="flex items-center gap-1">
                {who.worker ? <WorkerBadge kind={who.worker} size="sm" /> : null}
                {who.name} · {LEVEL_LABEL[g.level]}
              </li>
            );
          })}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
